import { Board } from './board.js';
import { createRandomPiece } from './piece.js';
import { resolveMusicalClears } from './musicMatcher.js';
import { Renderer } from './renderer.js';
import { MATCH_GLOW } from './scale.js';

const BASE_DROP_MS = 900;
const LINES_PER_LEVEL = 5;
const CLING_IMG = '/junior-assets/images/espiritumusical.png';

export class MusicalCloudTetrisGame {
  constructor(ui) {
    this.ui = ui;
    this.board = new Board();
    this.renderer = new Renderer(ui.boardCanvas, ui.nextCanvas, ui.boardFrame, ui.panelEl, ui.mountEl);
    this.active = null;
    this.next = createRandomPiece();
    this.score = 0;
    this.level = 1;
    this.lines = 0;
    this.combo = 0;
    this.gameOver = false;
    this.paused = false;
    this.dropTimer = 0;
    this.lastTs = 0;
    this.rafId = null;
  }

  start() {
    this.board.reset();
    this.score = 0;
    this.level = 1;
    this.lines = 0;
    this.combo = 0;
    this.gameOver = false;
    this.paused = false;
    this.next = createRandomPiece();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.renderer.applyLayout();
      });
    });
    this.spawnPiece();
    this.updateHud();
    this.lastTs = 0;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame((ts) => this.loop(ts));
  }

  spawnPiece() {
    this.active = this.next;
    this.next = createRandomPiece();
    if (this.board.collides(this.active)) {
      this.gameOver = true;
      this.active = null;
      if (this.ui.statusEl) this.ui.statusEl.textContent = 'Fin del juego — pulsa R';
    }
  }

  get dropInterval() {
    return Math.max(120, BASE_DROP_MS - (this.level - 1) * 70);
  }

  loop(ts) {
    if (!this.lastTs) this.lastTs = ts;
    const dt = ts - this.lastTs;
    this.lastTs = ts;

    if (!this.paused && !this.gameOver && this.active) {
      this.dropTimer += dt;
      if (this.dropTimer >= this.dropInterval) {
        this.dropTimer = 0;
        this.softDrop();
      }
    }

    this.renderer.renderBoard(this.board, this.active, ts);
    this.renderer.renderNext(this.next);
    this.rafId = requestAnimationFrame((t) => this.loop(t));
  }

  softDrop() {
    if (!this.active || this.gameOver) return;
    if (!this.tryMove(0, 1)) this.lockActive();
  }

  hardDrop() {
    if (!this.active || this.gameOver) return;
    while (this.tryMove(0, 1)) {
      /* cae */
    }
    this.lockActive();
  }

  tryMove(dx, dy) {
    if (!this.active) return false;
    this.active.move(dx, dy);
    if (this.board.collides(this.active)) {
      this.active.move(-dx, -dy);
      return false;
    }
    return true;
  }

  tryRotate() {
    if (!this.active || this.gameOver) return;
    const backupMatrix = this.active.matrix.map((r) => [...r]);
    const backupNotes = this.active.noteGrid.map((r) => r.map((c) => (c ? { ...c } : null)));
    const backupX = this.active.x;
    const backupY = this.active.y;

    this.active.rotate();
    if (this.board.collides(this.active)) {
      const kicks = [-1, 1, -2, 2];
      let moved = false;
      for (const k of kicks) {
        this.active.x = backupX + k;
        if (!this.board.collides(this.active)) {
          moved = true;
          break;
        }
      }
      if (!moved) {
        this.active.matrix = backupMatrix;
        this.active.noteGrid = backupNotes;
        this.active.x = backupX;
        this.active.y = backupY;
      }
    }
  }

  triggerClingDance() {
    const cling = this.ui.clingEl;
    if (!cling) return;
    cling.classList.remove('mct-cling--dance');
    void cling.offsetWidth;
    cling.classList.add('mct-cling--dance');
    window.setTimeout(() => cling.classList.remove('mct-cling--dance'), 1800);
  }

  triggerBoardFlash(rule) {
    const flash = this.ui.flashEl;
    if (!flash) return;
    const glow = MATCH_GLOW[rule];
    if (glow?.color) flash.style.setProperty('--mct-flash-color', glow.color);
    flash.classList.remove('mct-board-flash--on');
    void flash.offsetWidth;
    flash.classList.add('mct-board-flash--on');
    window.setTimeout(() => flash.classList.remove('mct-board-flash--on'), 550);
  }

  lockActive() {
    if (!this.active) return;
    this.board.lock(this.active);
    const { totalScore, matchLog } = resolveMusicalClears(this.board);

    if (matchLog.length > 0) {
      this.combo += matchLog.length;
      this.renderer.triggerMatchFx(matchLog);
      this.triggerClingDance();
      const last = matchLog[matchLog.length - 1];
      this.triggerBoardFlash(last.rule);

      if (this.ui.matchToast) {
        const label = MATCH_GLOW[last.rule]?.label ?? last.rule;
        const comboTxt = this.combo > 1 ? ` · Combo ×${this.combo}` : '';
        this.ui.matchToast.textContent = `✨ ${label}! +${totalScore}${comboTxt}`;
        this.ui.matchToast.classList.add('mct-match-toast--on');
        window.setTimeout(() => this.ui.matchToast?.classList.remove('mct-match-toast--on'), 1400);
      }
    } else {
      this.combo = 0;
    }

    if (totalScore > 0) {
      this.score += totalScore;
      this.lines += matchLog.length;
      this.level = 1 + Math.floor(this.lines / LINES_PER_LEVEL);
      this.updateHud();
    }

    if (this.board.isTopBlocked()) {
      this.gameOver = true;
      this.active = null;
      if (this.ui.statusEl) this.ui.statusEl.textContent = 'Fin del juego — pulsa R';
      return;
    }

    this.spawnPiece();
    this.dropTimer = 0;
  }

  updateHud() {
    this.ui.scoreEl.textContent = String(this.score).padStart(7, '0');
    this.ui.levelEl.textContent = String(this.level).padStart(2, '0');
    if (this.ui.comboEl) {
      this.ui.comboEl.textContent = this.combo > 1 ? `×${this.combo}` : '—';
      this.ui.comboEl.classList.toggle('mct-combo--hot', this.combo >= 3);
    }
    if (this.ui.harmonyFill) {
      const inLevel = this.lines % LINES_PER_LEVEL;
      const pct = Math.min(100, (inLevel / LINES_PER_LEVEL) * 100);
      this.ui.harmonyFill.style.width = `${pct}%`;
    }
  }

  handleKey(code) {
    if (this.gameOver) {
      if (code === 'KeyR' || code === 'Enter') this.start();
      return;
    }
    if (code === 'KeyP') {
      this.paused = !this.paused;
      if (this.ui.statusEl) this.ui.statusEl.textContent = this.paused ? 'Pausa' : '';
      return;
    }
    if (this.paused) return;

    switch (code) {
      case 'ArrowLeft':
        this.tryMove(-1, 0);
        break;
      case 'ArrowRight':
        this.tryMove(1, 0);
        break;
      case 'ArrowDown':
        this.softDrop();
        this.dropTimer = 0;
        break;
      case 'ArrowUp':
      case 'KeyX':
        this.tryRotate();
        break;
      case 'Space':
        this.hardDrop();
        break;
      default:
        break;
    }
  }

  destroy() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }
}

export function initMusicalCloudTetris(root) {
  root.innerHTML = `
    <div class="mct-construct-panel">
      <header class="mct-top-hud">
        <div class="mct-top-stat">
          <span class="mct-top-label">Puntos</span>
          <strong id="mct-score" class="mct-top-value">0000000</strong>
        </div>
        <div class="mct-top-stat">
          <span class="mct-top-label">Nivel</span>
          <strong id="mct-level" class="mct-top-value">01</strong>
        </div>
      </header>

      <div class="mct-body">
        <nav class="mct-toolbar" aria-label="Controles">
          <button type="button" id="mct-btn-pause" class="mct-tool-btn" title="Pausa (P)">⏸</button>
          <button type="button" id="mct-btn-restart" class="mct-tool-btn" title="Reiniciar (R)">↻</button>
        </nav>

        <div class="mct-stage-panel">
          <div class="mct-board-slot">
            <div id="mct-board-frame" class="mct-board-frame">
              <canvas id="mct-board" class="mct-board-canvas" aria-label="Tablero de juego"></canvas>
              <div id="mct-board-flash" class="mct-board-flash"></div>
              <p id="mct-match-toast" class="mct-match-toast junior-display"></p>
            </div>
          </div>
          <p id="mct-status" class="mct-status"></p>
        </div>

        <aside class="mct-rail-panel">
          <div class="mct-rail-next mct-panel--glass">
            <span class="mct-rail-label">Siguiente</span>
            <canvas id="mct-next" class="mct-next-canvas" aria-label="Siguiente pieza"></canvas>
          </div>
          <div class="mct-rail-combo mct-panel--glass">
            <span class="mct-rail-label">Combo</span>
            <strong id="mct-combo" class="mct-combo-value">—</strong>
          </div>
          <img id="mct-cling" class="mct-cling" src="${CLING_IMG}" alt="Cling" draggable="false" />
          <section class="mct-rules-palette" aria-label="Reglas musicales">
            <h2 class="mct-rules-heading">Puntuación musical</h2>
            <div class="mct-rules-grid">
              <article class="mct-rule-card mct-rule-card--octava">
                <span class="mct-rule-icon" aria-hidden="true">
                  <span class="mct-icon-note mct-icon-note--c">C</span>
                  <span class="mct-icon-note mct-icon-note--c">C</span>
                  <span class="mct-icon-note mct-icon-note--c">C</span>
                </span>
                <div><strong>Octava</strong><p>3 notas iguales</p></div>
              </article>
              <article class="mct-rule-card mct-rule-card--triada">
                <span class="mct-rule-icon" aria-hidden="true">
                  <span class="mct-icon-note mct-icon-note--c">C</span>
                  <span class="mct-icon-note mct-icon-note--e">E</span>
                  <span class="mct-icon-note mct-icon-note--g">G</span>
                </span>
                <div><strong>Tríada</strong><p>tríada mayor</p></div>
              </article>
              <article class="mct-rule-card mct-rule-card--tetracord">
                <span class="mct-rule-icon mct-rule-icon--steps" aria-hidden="true">
                  <span>C</span><span>D</span><span>E</span><span>F</span>
                </span>
                <div><strong>Tetracordio</strong><p>4 notas de escala</p></div>
              </article>
              <article class="mct-rule-card mct-rule-card--escala">
                <span class="mct-rule-icon mct-rule-icon--rainbow" aria-hidden="true">Do→Do</span>
                <div><strong>Escala</strong><p>Do–Do (8)</p></div>
              </article>
            </div>
          </section>
        </aside>
      </div>

      <footer class="mct-harmony-bar">
        <span class="mct-harmony-label">Progreso musical</span>
        <div class="mct-harmony-track" role="progressbar" aria-valuemin="0" aria-valuemax="100">
          <div id="mct-harmony-fill" class="mct-harmony-fill"></div>
        </div>
        <span class="mct-scale-badge"><span class="mct-scale-badge-icon">𝄞</span> Do Mayor</span>
      </footer>
    </div>
  `;

  const panelEl = root.querySelector('.mct-construct-panel');
  const game = new MusicalCloudTetrisGame({
    mountEl: root,
    panelEl,
    boardFrame: root.querySelector('#mct-board-frame'),
    boardCanvas: root.querySelector('#mct-board'),
    nextCanvas: root.querySelector('#mct-next'),
    scoreEl: root.querySelector('#mct-score'),
    levelEl: root.querySelector('#mct-level'),
    comboEl: root.querySelector('#mct-combo'),
    statusEl: root.querySelector('#mct-status'),
    clingEl: root.querySelector('#mct-cling'),
    matchToast: root.querySelector('#mct-match-toast'),
    harmonyFill: root.querySelector('#mct-harmony-fill'),
    flashEl: root.querySelector('#mct-board-flash'),
  });

  root.querySelector('#mct-btn-pause')?.addEventListener('click', () => game.handleKey('KeyP'));
  root.querySelector('#mct-btn-restart')?.addEventListener('click', () => {
    if (game.gameOver) game.handleKey('KeyR');
    else game.start();
  });

  const onKeyDown = (e) => {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Space'].includes(e.code)) {
      e.preventDefault();
    }
    game.handleKey(e.code);
  };

  window.addEventListener('keydown', onKeyDown);
  game.start();

  const ro = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver(() => game.renderer.applyLayout())
    : null;
  ro?.observe(panelEl);
  ro?.observe(root);
  const boardSlot = root.querySelector('.mct-board-slot');
  if (boardSlot) ro?.observe(boardSlot);
  const onResize = () => game.renderer.applyLayout();
  window.addEventListener('resize', onResize);

  return () => {
    ro?.disconnect();
    window.removeEventListener('resize', onResize);
    window.removeEventListener('keydown', onKeyDown);
    game.destroy();
    root.innerHTML = '';
  };
}
